import {
	iconEye,
	iconEyeSlash
} from './_icons';

/**
 * Adds an eye icon to toggle password visibility
 * Does NOT wrap the field - keeps it in original DOM position to preserve Safari autofill
 */
export default class PasswordField {
    /**
     * Adds an eye icon to toggle password visibility
     * @param {Node} $field The Node of the password field
     */
    constructor( $field ) {
        // Check if the field exists
        if ( ! $field ) {
            return;
        }

        // Check if eye icon already exists (as next sibling)
        const nextSibling = $field.nextElementSibling;
        if ( nextSibling && nextSibling.classList && nextSibling.classList.contains( this.cssClasses.passwordVisibilityToggle ) ) {
            return;
        }

        // Save Node
        this.$field = $field;
        
        // Change class from "input" to "uncannyowl-input"
        if ( this.$field.classList.contains( 'input' ) ) {
            this.$field.classList.remove( 'input' );
            this.$field.classList.add( 'uncannyowl-input' );
        }

        // Make parent position relative if it isn't already (for absolute positioning of icon)
        this.setupParentPositioning();

        // Add padding to field for the icon
        this.addFieldPadding();

        // Add eye icon as sibling (NOT wrapping the field)
        this.addEyeIcon();

        // Ensure field stays editable
        this.ensureFieldEditable();
        
        // Add focus/blur handlers to style eye icon when field is focused
        this.setupFocusHandlers();
    }

    /**
     * Make parent position relative for absolute positioning of icon
     */
    setupParentPositioning() {
        const parent = this.$field.parentNode;
        
        // Check if parent already has position
        const parentPosition = window.getComputedStyle( parent ).position;
        
        // If parent doesn't have position, make it relative
        if ( parentPosition === 'static' ) {
            parent.style.position = 'relative';
        }
    }

    /**
     * Add padding to the right of the field to make room for the icon
     */
    addFieldPadding() {
        // Add padding-right to make room for the eye icon
        const currentPadding = window.getComputedStyle( this.$field ).paddingRight;
        const paddingValue = parseInt( currentPadding ) || 0;
        
        // Add 35px padding if not already enough
        if ( paddingValue < 35 ) {
            this.$field.style.paddingRight = '35px';
        }
    }

    /**
     * Add eye icon as a sibling element (not wrapping the field)
     */
    addEyeIcon() {
        // Create eye icon container
        this.$eyeIcon = document.createElement( 'div' );
        this.$eyeIcon.classList.add( this.cssClasses.passwordVisibilityToggle );
        this.$eyeIcon.innerHTML = iconEye;
        
        // Insert icon as sibling after the field
        this.$field.parentNode.insertBefore( this.$eyeIcon, this.$field.nextSibling );
        
        // Add click event to toggle visibility
        this.$eyeIcon.addEventListener( 'click', () => {
            const passwordIsVisible = this.$field.getAttribute( 'type' ) === 'text';

            if ( passwordIsVisible ) {
                // Hide the password
                this.$field.setAttribute( 'type', 'password' );
                this.$eyeIcon.innerHTML = iconEye;
            } else {
                // Show the password
                this.$field.setAttribute( 'type', 'text' );
                this.$eyeIcon.innerHTML = iconEyeSlash;
            }
        } );
    }
    
    /**
     * Setup focus/blur handlers to style eye icon when field is focused
     */
    setupFocusHandlers() {
        // Listen for focus on field
        this.$field.addEventListener( 'focus', () => {
            // Add focus class to eye icon
            if ( this.$eyeIcon ) {
                this.$eyeIcon.classList.add( 'ult-form-password-wrapper-eye-toggle--focus' );
            }
        } );
        
        // Listen for blur on field
        this.$field.addEventListener( 'blur', () => {
            // Remove focus class from eye icon
            if ( this.$eyeIcon ) {
                this.$eyeIcon.classList.remove( 'ult-form-password-wrapper-eye-toggle--focus' );
            }
        } );
    }

    /**
     * Ensure field stays editable
     */
    ensureFieldEditable() {
        // Remove readonly/disabled immediately
        this.$field.removeAttribute( 'readonly' );
        this.$field.removeAttribute( 'disabled' );
        this.$field.readOnly = false;
        this.$field.disabled = false;
    }

    /**
     * Object with the CSS classes used
     */
    get cssClasses() {
        return {
            passwordVisibilityToggle: 'ult-form-password-wrapper-eye-toggle'
        }
    }
}
